// ZipWorkerService - WebWorker management with fallback to main thread
// Phase 3B: Performance Optimization & Monitoring

import { memoryMonitorService } from "./MemoryMonitorService";
// Import worker with Vite's ?worker syntax for proper bundling
import ZipWorker from "../workers/zipWorker.js?worker";

interface ZipWorkerOptions {
  enableFallback?: boolean;
  workerPath?: string;
  timeoutMs?: number;
  maxRetries?: number;
}

interface ZipOperationProgress {
  progress: number;
  stage: string;
  message: string;
  processedFiles?: number;
  totalFiles?: number;
  metadata?: Record<string, unknown>;
}

interface ZipCreationParams {
  files: Array<{
    name: string;
    data: ArrayBuffer | Uint8Array;
    type: "track" | "cover" | "generic";
    trackNumber?: number;
  }>;
  options?: {
    compressionLevel?: number;
  };
}

interface ZipWorkerResult<T> {
  success: boolean;
  result?: T;
  error?: string;
  fallbackUsed: boolean;
  executionTime: number;
  memoryUsage: {
    start: number;
    end: number;
    peak: number;
  };
}

type ProgressCallback = (progress: ZipOperationProgress) => void;

class ZipWorkerService {
  private worker: Worker | null = null;
  private isWorkerAvailable = false;
  private pendingOperations = new Map<
    string,
    {
      resolve: (value: unknown) => void;
      reject: (error: unknown) => void;
      onProgress?: ProgressCallback;
      startTime: number;
      memoryStart: number;
    }
  >();

  private readonly options: Required<ZipWorkerOptions>;

  private retryCount = new Map<string, number>();
  private fallbackMode = false;

  // Phase 3B: Dynamic worker path resolution for production
  private resolveWorkerPath(): string {
    // Use relative path from shared-components to the worker file
    // Vite will resolve this correctly in both dev and production
    try {
      // Create URL relative to this service file location
      const workerUrl = new URL("../workers/zipWorker.js", import.meta.url);
      return workerUrl.href;
    } catch (error) {
      console.warn(
        "ZipWorkerService: Failed to resolve worker path, using fallback",
        error,
      );
      // Fallback for environments where import.meta.url isn't available
      return "/workers/zipWorker.js";
    }
  }

  constructor(options?: ZipWorkerOptions) {
    // Phase 3B: Initialize options with dynamic worker path
    const defaultOptions = {
      enableFallback: true,
      workerPath: this.resolveWorkerPath(),
      timeoutMs: 300000, // 5 minutes
      maxRetries: 2,
    };

    this.options = { ...defaultOptions, ...options };
    this.initializeWorker();
  }

  /**
   * Initialize WebWorker
   */
  private async initializeWorker(): Promise<void> {
    if (!this.isWorkerSupported()) {
      console.warn(
        "ZipWorkerService: WebWorker not supported, using fallback mode",
      );
      this.fallbackMode = true;
      return;
    }

    try {
      // Create worker using Vite's imported worker
      this.worker = new ZipWorker();

      this.worker.onmessage = this.handleWorkerMessage.bind(this);
      this.worker.onerror = this.handleWorkerError.bind(this);

      // Wait for worker ready signal
      const readyPromise = new Promise<void>((resolve, reject) => {
        const timeout = setTimeout(() => {
          reject(new Error("Worker initialization timeout"));
        }, 5000);

        const messageHandler = (event: MessageEvent) => {
          if (event.data.type === "ready") {
            clearTimeout(timeout);
            this.worker!.removeEventListener("message", messageHandler);
            resolve();
          }
        };

        this.worker!.addEventListener("message", messageHandler);
      });

      await readyPromise;
      this.isWorkerAvailable = true;
    } catch (error) {
      console.warn(
        "ZipWorkerService: Failed to initialize worker, using fallback",
        error,
      );
      this.fallbackMode = true;
      this.cleanup();
    }
  }

  /**
   * Handle worker messages
   */
  private handleWorkerMessage(event: MessageEvent): void {
    const { type, id, progress, result, error, message: _message } = event.data;

    // Handle worker ready signal (no operation ID)
    if (type === "ready" && !id) {
      // Worker is ready, no action needed as we already handle this in initializeWorker
      return;
    }

    const operation = this.pendingOperations.get(id);
    if (!operation) {
      console.warn(
        `ZipWorkerService: Received message for unknown operation ${id}`,
      );
      return;
    }

    switch (type) {
      case "progress":
        if (operation.onProgress) {
          operation.onProgress(progress);
        }
        break;

      case "complete":
        this.completeOperation(id, result, false);
        break;

      case "error":
        this.failOperation(id, error, false);
        break;

      case "cancelled":
        this.failOperation(id, "Operation cancelled", false);
        break;

      default:
        console.warn(`ZipWorkerService: Unknown message type: ${type}`);
    }
  }

  /**
   * Handle worker errors
   */
  private handleWorkerError(error: ErrorEvent): void {
    console.error("ZipWorkerService: Worker error", error);

    // Fail all pending operations
    for (const [id] of this.pendingOperations) {
      this.failOperation(id, "Worker error occurred", false);
    }

    // Switch to fallback mode
    this.fallbackMode = true;
    this.cleanup();
  }

  /**
   * Create ZIP file from files
   */
  async createZip(
    params: ZipCreationParams,
    onProgress?: ProgressCallback,
  ): Promise<
    ZipWorkerResult<{ zipData: ArrayBuffer; size: number; fileCount: number }>
  > {
    const operationId = this.generateOperationId();
    const memoryStart = memoryMonitorService.getCurrentMemoryStatus().heapUsed;

    // Check memory before starting
    const memoryCheck = memoryMonitorService.canStartUpload(
      this.estimateMemoryUsage(params.files),
    );

    if (!memoryCheck.allowed) {
      return {
        success: false,
        error: memoryCheck.reason,
        fallbackUsed: false,
        executionTime: 0,
        memoryUsage: {
          start: memoryStart,
          end: memoryStart,
          peak: memoryStart,
        },
      };
    }

    const startTime = Date.now();

    try {
      if (this.shouldUseFallback()) {
        return await this.createZipFallback(
          params,
          onProgress,
          operationId,
          startTime,
          memoryStart,
        );
      } else {
        return await this.createZipWorker(
          params,
          onProgress,
          operationId,
          startTime,
          memoryStart,
        );
      }
    } catch (error) {
      // Retry with fallback if worker fails
      if (!this.fallbackMode && this.options.enableFallback) {
        console.warn(
          "ZipWorkerService: Worker failed, retrying with fallback",
          error,
        );
        return await this.createZipFallback(
          params,
          onProgress,
          operationId,
          startTime,
          memoryStart,
        );
      }

      throw error;
    }
  }

  /**
   * Create ZIP using WebWorker
   */
  private async createZipWorker(
    params: ZipCreationParams,
    onProgress: ProgressCallback | undefined,
    operationId: string,
    startTime: number,
    memoryStart: number,
  ): Promise<
    ZipWorkerResult<{ zipData: ArrayBuffer; size: number; fileCount: number }>
  > {
    return new Promise((resolve, reject) => {
      // Store operation
      this.pendingOperations.set(operationId, {
        resolve: (result) => {
          const executionTime = Date.now() - startTime;
          const memoryEnd =
            memoryMonitorService.getCurrentMemoryStatus().heapUsed;
          const memoryPeak = Math.max(memoryStart, memoryEnd); // Simplified peak calculation

          resolve({
            success: true,
            result,
            fallbackUsed: false,
            executionTime,
            memoryUsage: {
              start: memoryStart,
              end: memoryEnd,
              peak: memoryPeak,
            },
          });
        },
        reject: (error) => {
          const executionTime = Date.now() - startTime;
          const memoryEnd =
            memoryMonitorService.getCurrentMemoryStatus().heapUsed;

          resolve({
            success: false,
            error: error.message || error,
            fallbackUsed: false,
            executionTime,
            memoryUsage: {
              start: memoryStart,
              end: memoryEnd,
              peak: memoryEnd,
            },
          });
        },
        onProgress,
        startTime,
        memoryStart,
      });

      // Send message to worker
      this.worker!.postMessage({
        type: "createZip",
        id: operationId,
        data: params,
      });

      // Set timeout
      setTimeout(() => {
        if (this.pendingOperations.has(operationId)) {
          this.failOperation(operationId, "Operation timeout", false);
        }
      }, this.options.timeoutMs);
    });
  }

  /**
   * Create ZIP using main thread fallback
   */
  private async createZipFallback(
    params: ZipCreationParams,
    onProgress: ProgressCallback | undefined,
    operationId: string,
    startTime: number,
    memoryStart: number,
  ): Promise<
    ZipWorkerResult<{ zipData: ArrayBuffer; size: number; fileCount: number }>
  > {
    // Dynamic import of JSZip to avoid loading it unless needed
    const JSZip = (await import("jszip")).default;

    const zip = new JSZip();

    // Progress reporting
    const reportProgress = (
      progress: number,
      stage: string,
      message: string,
    ) => {
      if (onProgress) {
        onProgress({ progress, stage, message });
      }
    };

    try {
      reportProgress(
        0,
        "initializing",
        "Starting ZIP creation (fallback mode)...",
      );

      // Add files to ZIP
      let processedFiles = 0;
      for (const fileInfo of params.files) {
        const { name, data, type, trackNumber } = fileInfo;

        if (type === "track") {
          const paddedNumber = (trackNumber || processedFiles + 1)
            .toString()
            .padStart(2, "0");
          const cleanName = this.sanitizeFileName(name);
          zip.file(`${paddedNumber}-${cleanName}`, data);
        } else if (type === "cover") {
          const extension = this.getFileExtension(name) || "jpg";
          zip.file(`cover.${extension}`, data);
        } else {
          zip.file(this.sanitizeFileName(name), data);
        }

        processedFiles++;
        const progress = Math.floor(
          (processedFiles / params.files.length) * 50,
        );
        reportProgress(
          progress,
          "processing",
          `Processing file ${processedFiles} of ${params.files.length}`,
        );
      }

      reportProgress(50, "generating", "Generating ZIP file...");

      // Generate ZIP
      const zipData = await zip.generateAsync(
        {
          type: "uint8array",
          compression: "DEFLATE",
          compressionOptions: {
            level: params.options?.compressionLevel || 6,
          },
        },
        (metadata: { percent: number; [key: string]: unknown }) => {
          const progress = 50 + Math.floor(metadata.percent / 2);
          reportProgress(
            progress,
            "generating",
            `Generating ZIP: ${metadata.percent.toFixed(1)}%`,
          );
        },
      );

      reportProgress(100, "complete", "ZIP creation complete");

      const executionTime = Date.now() - startTime;
      const memoryEnd = memoryMonitorService.getCurrentMemoryStatus().heapUsed;

      return {
        success: true,
        result: {
          zipData: zipData.buffer,
          size: zipData.byteLength,
          fileCount: params.files.length,
        },
        fallbackUsed: true,
        executionTime,
        memoryUsage: {
          start: memoryStart,
          end: memoryEnd,
          peak: Math.max(memoryStart, memoryEnd),
        },
      };
    } catch (error: CatchError) {
      const executionTime = Date.now() - startTime;
      const memoryEnd = memoryMonitorService.getCurrentMemoryStatus().heapUsed;

      return {
        success: false,
        error: error.message,
        fallbackUsed: true,
        executionTime,
        memoryUsage: {
          start: memoryStart,
          end: memoryEnd,
          peak: memoryEnd,
        },
      };
    }
  }

  /**
   * Cancel operation
   */
  cancelOperation(operationId: string): boolean {
    if (!this.pendingOperations.has(operationId)) {
      return false;
    }

    if (this.worker && !this.fallbackMode) {
      this.worker.postMessage({
        type: "cancel",
        id: operationId,
      });
    }

    this.failOperation(operationId, "Operation cancelled", this.fallbackMode);
    return true;
  }

  /**
   * Utility methods
   */

  private shouldUseFallback(): boolean {
    return this.fallbackMode || !this.isWorkerAvailable;
  }

  private isWorkerSupported(): boolean {
    return typeof Worker !== "undefined" && typeof window !== "undefined";
  }

  private generateOperationId(): string {
    return `zip_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private estimateMemoryUsage(files: ZipCreationParams["files"]): number {
    // Estimate total memory usage (original files + ZIP overhead)
    const totalSize = files.reduce((sum, file) => {
      const size = file.data.byteLength || file.data.length || 0;
      return sum + size;
    }, 0);

    // Add 50% overhead for ZIP processing
    return totalSize * 1.5;
  }

  private sanitizeFileName(fileName: string): string {
    return fileName
      .replace(/[<>:"/\\|?*]/g, "_")
      .replace(/\s+/g, "_")
      .toLowerCase();
  }

  private getFileExtension(fileName: string): string | null {
    const match = fileName.match(/\.([^.]+)$/);
    return match ? match[1].toLowerCase() : null;
  }

  private completeOperation(
    id: string,
    result: unknown,
    fallbackUsed: boolean,
  ): void {
    const operation = this.pendingOperations.get(id);
    if (operation) {
      this.pendingOperations.delete(id);
      this.retryCount.delete(id);
      operation.resolve(result);
    }
  }

  private failOperation(
    id: string,
    error: string,
    fallbackUsed: boolean,
  ): void {
    const operation = this.pendingOperations.get(id);
    if (operation) {
      // Check if we should retry
      const currentRetries = this.retryCount.get(id) || 0;
      if (
        currentRetries < this.options.maxRetries &&
        !fallbackUsed &&
        this.options.enableFallback
      ) {
        this.retryCount.set(id, currentRetries + 1);
        console.warn(
          `ZipWorkerService: Retrying operation ${id} (attempt ${currentRetries + 1})`,
        );
        return;
      }

      this.pendingOperations.delete(id);
      this.retryCount.delete(id);
      operation.reject(new Error(error));
    }
  }

  private cleanup(): void {
    if (this.worker) {
      this.worker.terminate();
      this.worker = null;
    }
    this.isWorkerAvailable = false;
  }

  /**
   * Get service status
   */
  getStatus() {
    return {
      workerAvailable: this.isWorkerAvailable,
      fallbackMode: this.fallbackMode,
      pendingOperations: this.pendingOperations.size,
      workerSupported: this.isWorkerSupported(),
    };
  }

  /**
   * Ping worker (health check)
   */
  async pingWorker(): Promise<boolean> {
    if (!this.worker || this.fallbackMode) {
      return false;
    }

    return new Promise((resolve) => {
      const pingId = this.generateOperationId();
      const timeout = setTimeout(() => resolve(false), 5000);

      const messageHandler = (event: MessageEvent) => {
        if (event.data.type === "pong" && event.data.id === pingId) {
          clearTimeout(timeout);
          this.worker!.removeEventListener("message", messageHandler);
          resolve(true);
        }
      };

      this.worker.addEventListener("message", messageHandler);
      this.worker.postMessage({
        type: "ping",
        id: pingId,
      });
    });
  }

  /**
   * Cleanup on service destruction
   */
  destroy(): void {
    // Cancel all pending operations
    for (const [id] of this.pendingOperations) {
      this.cancelOperation(id);
    }

    this.cleanup();
    this.pendingOperations.clear();
    this.retryCount.clear();
  }
}

// Export singleton instance
export const zipWorkerService = new ZipWorkerService();

export type {
  ZipWorkerOptions,
  ZipCreationParams,
  ZipOperationProgress,
  ZipWorkerResult,
};
